import { utcToZonedTime, formatInTimeZone, toDate } from 'date-fns-tz'
import { addMinutes, differenceInCalendarDays, formatRelative, format as fd } from 'date-fns'
import { sv } from 'date-fns/locale'
import { hasInformationTag } from '~/utils/data'
import { Icon } from '~/components'

// -----------------------------------------------------
//  Dates 
// -----------------------------------------------------

export const defaultTz = 'Europe/Stockholm'

// Format Date object for datetime-local input
export const formatLocalDateTime = date => formatInTimeZone(date, defaultTz, "yyyy-MM-dd'T'HH:mm")

// Parse datetime-local string to Date object 
export const parseLocalDateTime = value => toDate(`${value}:00`, { timeZone: defaultTz })

// Format Date object to timestamp used in API requests
export const formatTimestamp = date => formatInTimeZone(date, defaultTz, "yyyy-MM-dd'T'HH:mm:ssxxx")

// Format readable date and time
export const formatReadable = date => formatInTimeZone(date, defaultTz, 'yyyy-MM-dd HH:mm')

// Format readable date only
export const formatReadableDateOnly = date => formatInTimeZone(date, defaultTz, 'yyyy-MM-dd')

// Format time
export const formatTime = date => formatInTimeZone(date, defaultTz, 'HH:mm')

// Format localized readable date, example: 20:e december kl. 12:30
export const formatLocalized = date => APP.lang === 'sv' ? fd(date, 'do MMMM \'kl.\' p', { locale: sv }) : fd(date, 'do MMMM p')

// Return a relative or a formatted date
export const formatReadableDate = date => {
  date = utcToZonedTime(date, defaultTz)
  const now = utcToZonedTime(new Date(), defaultTz)
  if (differenceInCalendarDays(date, now) < 2) {
    return APP.lang === 'sv' ?
      formatRelative(date, now, { locale: sv }) :
      formatRelative(date, now)
  }
  return formatLocalized(date)
}


// -----------------------------------------------------
//  User 
// -----------------------------------------------------

export const getUserName = user => {
  let userName = ''
  if (user && user.firstName !== undefined) userName += user.firstName
  if (user && user.lastName !== undefined) userName += userName !== '' ? ' ' + user.lastName : user.lastName
  return userName
}

// Normalize a hand typed phone number to E.164. The OTP flow gets the country
// code from PhoneInput, but the profile form is a plain text field so we have
// to infer it: 00 is the international prefix and a bare leading 0 is national.
export const formatPhoneNumber = (value, defaultDialCode = '+46') => {
  const phone = (value || '').replace(/[\s\-()./]/g, '')
  if (phone.startsWith('+')) return phone
  if (phone.startsWith('00')) return '+' + phone.substr(2)
  return defaultDialCode + phone.replace(/^0/, '')
}


// -----------------------------------------------------
//  Addresses etc.
// -----------------------------------------------------

// NOTE: This is the old version used with v2 API, you should instead use the functions below
export function formatAddress(address, streetNumber) {
  if (streetNumber) {
    return `${address.streetName} ${streetNumber}${address.city ? ', ' + address.city : ''}`
  }
  return `${address.streetName}${address.streetNumber ? ' ' + address.streetNumber : ''}${address.entrance ? ' ' + address.entrance : ''}${address.city ? ', ' + address.city : ''}`
}

export function formatStreetAddress(location) {
  const sa = []
  if (location?.address) {
    const a = location.address
    if (a?.streetName) sa.push(a?.streetName)
    if (a?.streetNumber) {
      let sn = a?.streetNumber
      if (a?.entrance) sn += a?.entrance
      sa.push(sn)
    }
  }
  return sa.join(' ')
}

export function formatAddressParts(location) {
  const title = []
  const subtitle = []
  const sa = formatStreetAddress(location)
  if (location?.locationType !== 'STREET' && (location?.address?.poiName || location?.name)) {
    title.push(location.name || location.address.poiName)
    if (sa) {
      subtitle.push(sa)
    }
  }
  else {
    if (sa) {
      title.push(sa)
    }
  }
  if (location?.address?.city) {
    subtitle.push(location?.address?.city)
  }
  if (location?.address?.municipality && !subtitle.includes(location?.address?.municipality)) {
    subtitle.push(location?.address?.municipality)
  }
  return {
    title: title.join(' '),
    subtitle: subtitle.join(', ')
  }
}

export function formatLocationTitle(location) {
  const title = []

  if (location?.locationType !== 'STREET' && (location?.address?.poiName || location?.name)) {
    title.push(location.name || location.address.poiName)
  }
  else {
    const sa = formatStreetAddress(location)
    if (sa) {
      title.push(sa)
    }
  }
  if (location?.address?.city) title.push(location?.address?.city)
  return title.join(', ')
}

// export function formatAddressForDropDown(address, streetNumber, styles) {
//   if (streetNumber) {
//     return <div className={styles.dropDown}><Icon id="pin" className={styles.dropDownPin}/><div className={styles.dropDownAddress}>{address.streetName} {streetNumber} {address.city && <span>{address.city}</span>}</div></div>
//   }
//   return <div className={styles.dropDown}><Icon id="pin" className={styles.dropDownPin}/><div className={styles.dropDownAddress}>{address.streetName}{address.streetNumber && ` ${address.streetNumber}`}{address.entrance && ` ${address.entrance}`}{address.city && <span>{address.city}</span>}</div></div>
// }

// NOTE: This is used to avoid a bug where TTES instances have different zone for the 
// same address resulting in a ZONE_ID_NOT_MATCHING error when doing price estimate.
// const filterAddress = (address, appendId) => {
//   delete address.zone
//   address.id = appendId;
//   return address
// }

export function formatLocationId(location) {
  if (location.id) return location.id
  else if (location.locationId) {
    console.log('FOUND LOCATION ID!')
    return location.locationId
  }
  else {
    const address = location.address
    return `${address.streetName}${address.streetNumber ? ' ' + address.streetNumber : ''}${address.entrance ? ' ' + address.entrance : ''}${address.city ? ' ' + address.city : ''}`
  }
}

function formatLocationForRequest(location) {
  return { id: formatLocationId(location), ...location.address }
}


// -----------------------------------------------------
//  API requests
// -----------------------------------------------------

export function formatLocationQuery(location) {
  if (location.id) return location.id
  else {
    const address = location.address
    return `${address.streetName}${address.streetNumber ? ' ' + address.streetNumber : ''}${address.entrance ? address.entrance : ''}${address.city ? ', ' + address.city : ''}`
  }
}

export function formatServiceAvailabiltyQuery(values, user) {
  const params = new URLSearchParams()
  // formatLocationId takes the location (id / locationId / address), not the bare address:
  // handing it the address threw on `address.address.streetName` for any location without an id.
  if (values.departure?.address) {
    params.append('fromLocationId', formatLocationId(values.departure))
  }
  if (values.destination?.address) {
    params.append('toLocationId', formatLocationId(values.destination))
  }
  if (values?.intermediateStopLocations?.length > 0) {
    const stops = values.intermediateStopLocations.filter(location => location?.address !== undefined)
    if (stops.length > 0) {
      params.append(
        'intermediateLocationIdList', 
        stops.map(location => formatLocationId(location)).toString()
      )
    }
  }
  if (values.pickupTime !== '') {
    params.append('dateTime', formatTimestamp(values.pickupTime))
  }
  if (user && user?.phone !== '') {
    params.append('phone', user.phone)
  }
  if (values?.account?.accountNumber && values?.account?.type) {
    params.append('accountNumber', values.account.accountNumber)
    params.append('accountType', values.account.type)
  }
  return params.toString()
}

export function formatPriceEstimateRequest(values, freshAvailability = null) {
  console.log("formatPriceEstimateRequest", values)
  const booking = {
    // originLocation: filterAddress(values.departure.address),
    // destinationLocation: filterAddress(values.destination.address),
    originLocation: formatLocationForRequest(values.departure),
    destinationLocation: formatLocationForRequest(values.destination),
    serviceTypes: values.serviceType === 'delivery' ? ['delivery'] : ['taxi', 'largeCar', 'business'],
    // vehicleOptions: values.vehicleOptions[0] === '' ? [] : values.vehicleOptions,
  }

  // v4: include providerIds for all available providers
  // freshAvailability is passed when called right after fetching (before formik state updates)
  const key = values.pickupTime === '' ? 'direct' : 'prebook'
  const avail = freshAvailability || values.availability?.[key]
  const servicesByProvider = avail?.servicesByProvider
  if (Array.isArray(servicesByProvider) && servicesByProvider.length > 0) {
    booking.providerIds = servicesByProvider.map(p => p.providerData.providerId)
  }
  

  if (values.pickupTime || values.autoValues.pickupTime) {
    booking.pickUpTimeStamp = formatTimestamp(values.autoValues.pickupTime || values.pickupTime)
  }

  // Private or company trip?
  if (values.company) {
    console.log("formatPriceEstimateRequest company", values.company)
    booking.companyId = values.company.id
  }

  if (values?.vouchers?.length > 0) {
    booking.voucherCodes = values.vouchers
  }

  if (values?.intermediateStopLocations?.length > 0) {
    const stops = values.intermediateStopLocations.filter(location => location?.address !== undefined)
    if (stops.length > 0) {
      // booking.intermediateStopLocations = stops.map(location => filterAddress(location.address))
      booking.intermediateStopLocations = stops.map(formatLocationForRequest)
    }
  }
  booking.vehicleOptions = getChargableOptions(values);
  console.log("formatPriceEstimateRequest booking", booking)
  return booking
}

export function getChargableOptions(values) {
  const chargableOptions = []
  if (values.rearFacingChildSeat) chargableOptions.push('rearFacingChildSeat')
  return chargableOptions;
}


export function getOptions(values) {
  const options = []
  if (values.pet) options.push('pet')
  if (values.low) options.push('low')
  if (values.stationWagon) options.push('stationWagon')
  if (values.rearFacingChildSeat) options.push('rearFacingChildSeat')
  if (values.booster === 1) options.push('boosterSeat1')
  if (values.booster === 2) options.push('boosterSeat2')
  if (values.electric) options.push('electric')
  return options
}

export function getPackages(values) {
  const packages = []
  if (values.ENVELOPE) packages.push({ packageType: 'ENVELOPE', count: values.ENVELOPE })
  if (values.BOX) packages.push({ packageType: 'BOX', count: values.BOX })
  if (values.BAG) packages.push({ packageType: 'BAG', count: values.BAG })
  if (values.MOVING_BOX) packages.push({ packageType: 'MOVING_BOX', count: values.MOVING_BOX })
  return packages
}

export function formatBookingRequest(values, userId) {
  const serviceType = (values.autoValues.serviceType || values.serviceType) === 'estate' ? 'taxi' : (values.autoValues.serviceType || values.serviceType);
  const options = getOptions(values);
  if((values.autoValues.serviceType || values.serviceType) === 'estate') options.push('stationWagon');
  const booking = {
    originLocation: formatLocationForRequest(values.departure),
    serviceType: serviceType,
    traveler: {
      name: values.name,
      phoneNumber: values.phone,
      ...(hasInformationTag(values, 'TRAVELER_COUNT_REQUIRED') && values.passengerCount > 0
        ? { travelerCount: values.passengerCount }
        : {})
    },
    generalComment: values.generalComment,
    userId: userId,
    vehicleOptions: options
  }

  // Got destination?
  if (values.destination?.address) {
    booking.destinationLocation = formatLocationForRequest(values.destination)
  }

  // Skip pickup time when direct booking
  if (values.pickupTime || values.autoValues.pickupTime) {
    booking.pickUpTimeStamp = formatTimestamp(values.autoValues.pickupTime || values.pickupTime)
  }

  // Private or company trip?
  if (values.company) {
    booking.companyId = values.company.id
  }

  // Add intermediate stops
  if (values?.intermediateStopLocations?.length > 0) {
    booking.intermediateStopLocations = values.intermediateStopLocations.map(formatLocationForRequest)
  }

  // Airport - only include flightInfo for pre-bookings, not direct bookings
  if (hasInformationTag(values, 'FLIGHT_NBR') && values.pickupTime !== '') {
    booking.originLocation.flightInfo = {
      flightNumber: values.flightNumber,
      checkedInBaggage: values.checkedInBaggage
    }
  }

  // Fixed price?
  const fixedPrice = values.autoValues.fixedPrice !== null ? values.autoValues.fixedPrice : values.fixedPrice
  if (fixedPrice && values.priceToken) {
    booking.priceToken = values.priceToken
  }

  // Add service availability token
  const key = values.pickupTime === '' ? 'direct' : 'prebook'
  const availData = values?.availability[key]
  if (availData?.servicesByProvider) {
    // v4: serviceToken is per-provider. Use the selected provider's token; when no provider
    // is explicitly selected (e.g. single-TSAB route where the partner section is hidden),
    // fall back to the first (and only) provider so the token is never silently dropped.
    const providerId = values.selectedProviderId || availData.servicesByProvider[0]?.providerData?.providerId
    const selectedProvider = availData.servicesByProvider.find(
      p => p.providerData.providerId === providerId
    )
    if (selectedProvider?.serviceToken) {
      booking.serviceToken = selectedProvider.serviceToken
    }
  } else if (availData?.serviceToken) {
    booking.serviceToken = availData.serviceToken
  }

  // Payment
  if (values.payment.indexOf('Account') !== -1) {
    booking.paymentMethod = values.account.type === 'TCA' ? 'TravelClearingAccount' : 'TSABAccount'
    booking.accountInfo = {
      type: values.account.type,
      number: values.account.accountNumber
    }
  }
  else if (values.payment.indexOf('CreditCard') !== -1) {
    booking.paymentMethod = 'CreditCard'
  }
  else {
    booking.paymentMethod = 'PayInCar'
  }

  // Add reference
  if (values.company || values.payment.indexOf('Account') !== -1) {
    booking.invoiceReference = values.accountReference
  }

  // Vouchers
  if (values?.vouchers?.length > 0) {
    booking.voucherCodes = values.vouchers
  }

  // Return trip: link back to the original booking (from its returnTripRules) so the
  // backend can register this booking as a return trip. See docs/RETURNTRIP.md.
  if (values.originalBookingReference) {
    booking.originalBookingReference = values.originalBookingReference
  }

  return booking
}

export function formatEditEstimateRequest(values) {
  const editableFields = values.editableFields || []
  const request = {}

  // Traveler info - always send if exists (either modified or original)
  const hasEditableName = editableFields.includes('traveler.name')
  const hasEditablePhone = editableFields.includes('traveler.phoneNumber')
  const hasOriginalName = values.booking?.traveler?.name
  const hasOriginalPhone = values.booking?.traveler?.phoneNumber

  if (hasEditableName || hasEditablePhone || hasOriginalName || hasOriginalPhone) {
    request.traveler = {}

    // Send name if editable (use current value) or if it existed originally
    if (hasEditableName) {
      request.traveler.name = values.name
    } else if (hasOriginalName) {
      request.traveler.name = values.booking.traveler.name
    }

    // Send phone if editable (use current value) or if it existed originally
    if (hasEditablePhone) {
      request.traveler.phoneNumber = values.phone
    } else if (hasOriginalPhone) {
      request.traveler.phoneNumber = values.booking.traveler.phoneNumber
    }
  }

  // General comment - always send if exists (either modified or original)
  if (editableFields.includes('generalComment')) {
    // If editable, use current value (could be empty if user cleared it)
    if (values.generalComment) {
      request.generalComment = values.generalComment
    }
  } else if (values.booking?.generalComment) {
    // If not editable, use original value
    request.generalComment = values.booking.generalComment
  }

  // Origin location - always include if we have it
  if (values.departure?.address) {
    request.originLocation = formatLocationForRequest(values.departure)
  }

  // Flight info - always include if there's data, even if not editable
  // The API requires these fields to be present in edit requests when they exist
  const hasFlightNumber = values.flightNumber && values.flightNumber.trim() !== ''
  const hasCheckedBaggage = values.checkedInBaggage === true

  if (hasFlightNumber || hasCheckedBaggage) {
    if (!request.originLocation) {
      request.originLocation = {}
    }
    request.originLocation.flightInfo = {}
    if (hasFlightNumber) {
      request.originLocation.flightInfo.flightNumber = values.flightNumber
    }
    if (hasCheckedBaggage !== undefined) {
      request.originLocation.flightInfo.checkedInBaggage = hasCheckedBaggage
    }
  }

  // Destination location - always include if we have it
  if (values.destination?.address) {
    request.destinationLocation = formatLocationForRequest(values.destination)
  }

  // Vehicle options - always send them (either modified or original)
  if (editableFields.includes('vehicleOptions')) {
    // If editable, use the current form values
    const serviceType = (values.autoValues.serviceType || values.serviceType) === 'estate' ? 'taxi' : (values.autoValues.serviceType || values.serviceType)
    const options = getOptions(values)
    if ((values.autoValues.serviceType || values.serviceType) === 'estate') {
      options.push('stationWagon')
    }
    request.vehicleOptions = options
  } else if (values.booking?.vehicleOptions) {
    // If not editable, use the original booking's vehicleOptions
    request.vehicleOptions = values.booking.vehicleOptions
  }

  // Invoice reference - always send if exists (either modified or original)
  if (editableFields.includes('invoiceReference')) {
    // If editable, use current value (could be empty if user cleared it)
    if (values.accountReference && values.accountReference.trim() !== '') {
      request.invoiceReference = values.accountReference
    }
  } else if (values.booking?.invoiceReference) {
    // If not editable, use original value
    request.invoiceReference = values.booking.invoiceReference
  }

  // Pickup timestamp - always include if there's a value, even if not editable
  // The API requires pickUpTimeStamp to be present in edit requests
  if (values.pickupTime || values.autoValues.pickupTime) {
    // In edit mode with unchanged time, use original pickupTime, not autoValues
    const timeToUse = editableFields.includes('pickUpTimeStamp')
      ? (values.autoValues.pickupTime || values.pickupTime)
      : values.pickupTime
    request.pickUpTimeStamp = formatTimestamp(timeToUse)
  }

  return request
}

export function formatPackageBookingRequest(values, userId) {

  // TODO: Skip pickup time when direct booking?
  const pickup = values.pickupTime 
    ? values.pickupTime
    : addMinutes(new Date(), 1)

  const booking = {
    pickUpTimeStamp: formatTimestamp(pickup),
    originLocation: formatLocationForRequest(values.departure),
    destinationLocation: formatLocationForRequest(values.destination),
    serviceType: values.serviceType,
    traveler: {
      name: values.name,
      phoneNumber: values.phone
    },
    userId: userId,
    priceToken: values.priceToken,
    packageDeliveryInfo: values.packageDeliveryInfo
  }

  // Do not send empty strings
  if (booking.packageDeliveryInfo.packageSender.nameOnDoor === '') {
    booking.packageDeliveryInfo.packageSender.nameOnDoor = values.name
  }
  if (booking.packageDeliveryInfo.packageSender.numberOfStairs === '') {
    delete booking.packageDeliveryInfo.packageSender.numberOfStairs
  }
  if (booking.packageDeliveryInfo.packageSender.doorCode === '') {
    delete booking.packageDeliveryInfo.packageSender.doorCode
  }

  if (booking.packageDeliveryInfo.packageReceiver.numberOfStairs === '') {
    delete booking.packageDeliveryInfo.packageReceiver.numberOfStairs
  }
  if (booking.packageDeliveryInfo.packageReceiver.doorCode === '') {
    delete booking.packageDeliveryInfo.packageReceiver.doorCode
  }

  // Packages
  const packages = []
  if (values.ENVELOPE > 0) {
    packages.push({ packageType: 'ENVELOPE', count: values.ENVELOPE })
  }
  if (values.BOX > 0) {
    packages.push({ packageType: 'BOX', count: values.BOX })
  }
  if (values.BAG > 0) {
    packages.push({ packageType: 'BAG', count: values.BAG })
  }
  if (values.MOVING_BOX > 0) {
    packages.push({ packageType: 'MOVING_BOX', count: values.MOVING_BOX })
  }
  booking.packageDeliveryInfo.packages = packages

  // Payment. Only send an account when one is actually selected; otherwise the
  // request would carry an empty accountInfo and the booking service rejects it.
  if (values.payment?.indexOf('Account') !== -1 && values.account?.accountNumber) {
    booking.paymentMethod = values.account.type === 'TCA' ? 'TravelClearingAccount' : 'TSABAccount'
    booking.accountInfo = {
      type: values.account.type,
      number: values.account.accountNumber
    }
    booking.invoiceReference = values.accountReference
  }
  else {
    booking.paymentMethod = 'PayInCar'
  }

  return booking
}

/*
export const getStatusText = booking => {
  // TODO: This should be replaced
  const COPY = window.COPY

  // Check if booking and bookingStatus exist to prevent errors during polling
  if (!booking?.bookingStatus?.statusCode) return '-'

  if (booking.bookingStatus.statusCode == 110) return i18next.t('status-waiting-for-confirmation')

  // 120 Confirmed
  if (booking.bookingStatus.statusCode == 120 || booking.bookingStatus.statusCode == 121) {
    return booking.bookingType === 'PRE_BOOKING' ? i18next.t('status-pre-booked') : i18next.t('status-searching')
  }
  // 220 Cancelled
  if (booking.bookingStatus.statusCode == 220) return COPY.booking.status_cancelled

  // 310 Dispatched
  if (booking.bookingStatus.statusCode == 310) return COPY.booking.status_dispatched

  // 320 AtPickup
  if (booking.bookingStatus.statusCode == 320) return COPY.booking.status_at_pickup

  // 410 EnRoute
  if (booking.bookingStatus.statusCode == 410) return COPY.booking.status_en_route

  // 510 Done
  if (booking.bookingStatus.statusCode == 510) return COPY.booking.status_done

  // 610 Terminated
  if (booking.bookingStatus.statusCode == 610) return COPY.booking.status_cancelled

  // 620 TerminatedNoShow
  if (booking.bookingStatus.statusCode == 620) return COPY.booking.status_no_show

  return '-'
}
*/

export function formatStatusRequest(bookings) {
  return bookings.map(booking => {
    // NOTE: Support anonymous bookings using the clientBookingToken
    return booking.clientBookingToken ?
      { bookingId: booking.id, clientBookingToken: booking.clientBookingToken } : { bookingId: booking.id }
  })
}
